import type { EditableBoat, RelationRow } from '@/modules/inventory/utils/boat-changes';

/**
 * #1408 / LEAK-01 (D-08) — the money keys the SPA must never send for a viewer
 * without `finance.cost.view`. Mirrors the server's MoneyFields write lists
 * (BOAT_WRITE_FIELDS, ASSET_WRITE_FIELDS); equipment row `cost` is handled by
 * stripMoneyKeys itself. `linked_trailer_cost_price` is read-only and never a
 * request field, but a stale form value must not ride along either.
 *
 * The server answers any non-null money value from a non-holder with a 422, so a
 * `0` default or a derived `total_cost` would block an ordinary inventory save.
 */
export const BOAT_MONEY_KEYS = [
  'cost_price',
  'boat_cost_price',
  'invoice_amount',
  'rigging',
  'freight',
  'total_cost',
  'linked_trailer_cost_price',
] as const;

export const ASSET_MONEY_KEYS = ['cost_price', 'invoice', 'freight'] as const;

const EQUIPMENT_LISTS = ['standard_equipment', 'optional_equipment'] as const;

function withoutCost(row: unknown): unknown {
  if (row === null || typeof row !== 'object' || Array.isArray(row)) return row;
  const { cost: _cost, ...rest } = row as Record<string, unknown>;
  return rest;
}

/**
 * The payload without its money keys (and without `cost` inside each
 * standard_equipment[] / optional_equipment[] row) when the viewer cannot see
 * cost. A holder's payload is returned as-is (same object).
 */
export function stripMoneyKeys<T extends object>(payload: T, keys: readonly string[], canViewCost: boolean): T {
  if (canViewCost) return payload;
  const out: Record<string, unknown> = { ...(payload as Record<string, unknown>) };
  for (const key of keys) delete out[key];
  for (const list of EQUIPMENT_LISTS) {
    const rows = out[list];
    if (Array.isArray(rows)) out[list] = rows.map(withoutCost);
  }
  return out as T;
}

function rowWithoutCost(row: RelationRow): RelationRow {
  const { cost: _cost, ...values } = row.values;
  return { ...row, values };
}

/**
 * The changes-model form of stripMoneyKeys: an EditableBoat with no money
 * scalar and no equipment cost, so a diff between two of them can never carry
 * money (and never derives total_cost). A holder's editable is returned as-is.
 */
export function stripEditableMoney(editable: EditableBoat, canViewCost: boolean): EditableBoat {
  if (canViewCost) return editable;
  return {
    ...editable,
    scalars: stripMoneyKeys(editable.scalars, BOAT_MONEY_KEYS, false),
    standard_equipment: editable.standard_equipment.map(rowWithoutCost),
    optional_equipment: editable.optional_equipment.map(rowWithoutCost),
  };
}
